import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo, { breadcrumbJsonLd, datasetJsonLd } from 'app/components/Seo';
import AppBar from 'app/components/Layout/AppBar';
import Footer from 'app/components/Footer';
import { SmoothScroll } from 'app/components/SmoothScroll';
import { Dataset, datasets, getDataset } from 'app/data/datasets';
import SpecificationsTable from './SpecificationsTable';
import CodeSample from './CodeSample';
import { buildCoverageContext, relatedDatasets } from './coverageContext';
import { Chip, DataLink, FOCUS, GROUND, Panel } from './ui';

/**
 * Per-dataset detail page — /datasets/:slug
 *
 * Every dataset in the registry becomes an addressable, linkable, citable asset
 * instead of a modal with no URL.
 *
 * Structure follows Databento's catalogue listing: identity, then the coverage
 * facts as chips immediately under the name, then the full specification table,
 * then the call that fetches it. Density and typography follow turbopuffer —
 * monospace for every key, value and numeric; sans for prose only; hairline
 * borders; no decorative imagery; no entrance animation.
 *
 * The registry is the only source of facts. Nothing dataset-specific on this
 * page is hand-written, and undefined fields are shown as gaps, never filled.
 */

const CATEGORY_LABEL: Record<Dataset['category'], string> = {
  equity: 'Equity',
  economic: 'Economic',
  sectorial: 'Sectorial',
};

/**
 * Page shell. Deliberately omits ParallaxBackground: it is decorative and
 * animated, and this surface is meant to read as an instrument panel. The
 * ground is solid so the notched panel legends have an exact colour to sit on.
 */
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SmoothScroll>
    <div
      className="relative overflow-hidden"
      style={{ backgroundColor: GROUND }}
    >
      <AppBar />
      <div className="min-h-screen pt-20 pb-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">{children}</div>
      </div>
      <Footer />
    </div>
  </SmoothScroll>
);

/**
 * Coverage facts, pulled up under the h1 Databento-style. Gaps included.
 *
 * One tight mono row: every value is a measured registry field, and every gap
 * is a dashed chip rather than an omission, so the row's length is an honest
 * signal of how much we actually know about this dataset.
 */
const IdentityChips: React.FC<{ dataset: Dataset }> = ({ dataset }) => (
  <div className="mt-3 flex flex-wrap items-center gap-1">
    <Chip>{CATEGORY_LABEL[dataset.category]}</Chip>

    {dataset.status === 'beta' ? (
      <Chip tone="beta">Beta</Chip>
    ) : (
      <Chip tone="accent">Available</Chip>
    )}

    {dataset.historyStart ? (
      <Chip>Since {dataset.historyStart.slice(0, 4)}</Chip>
    ) : (
      <Chip tone="gap">Start date not documented</Chip>
    )}

    {typeof dataset.entities === 'number' ? (
      <Chip>{dataset.entities.toLocaleString('en-US')}+ entities</Chip>
    ) : (
      <Chip tone="gap">Entity count not documented</Chip>
    )}

    {typeof dataset.columnCount === 'number' ? (
      <Chip>{dataset.columnCount.toLocaleString('en-US')} columns</Chip>
    ) : null}

    {dataset.universe ? (
      <Chip>{dataset.universe}</Chip>
    ) : (
      <Chip tone="gap">Universe not documented</Chip>
    )}

    {/* Undefined means no sov.data() endpoint exists — never render "undefined". */}
    {dataset.sdkQuery ? (
      <Chip>sov.data("{dataset.sdkQuery}")</Chip>
    ) : (
      <Chip tone="gap">No sov.data() endpoint</Chip>
    )}
  </div>
);

/**
 * Real constituents from an unfiltered pull. Rendered only where the registry
 * has them — most datasets were sampled with a ticker filter, so their tickers
 * would just echo the filter back. No slot is drawn when there is nothing true
 * to put in it.
 */
const Constituents: React.FC<{ dataset: Dataset }> = ({ dataset }) => {
  const tickers = dataset.sampleTickers;
  if (!tickers || tickers.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1">
      <span className="mr-1 font-mono text-[11px] uppercase tracking-[0.12em] text-gray-400">
        Observed constituents
      </span>
      {tickers.map(ticker => (
        <span
          key={ticker}
          className="inline-flex items-center border border-gray-800 bg-gray-900/80 px-1.5 py-px font-mono text-[11px] tabular-nums text-gray-300"
        >
          {ticker}
        </span>
      ))}
      <span className="ml-1 font-mono text-[11px] text-gray-400">
        sampled from a live pull, not the full universe
      </span>
    </div>
  );
};

// ---- Not found ---------------------------------------------------------------

const NotFound: React.FC<{ slug?: string }> = ({ slug }) => (
  <Shell>
    <Seo
      title="Dataset not found"
      description="No dataset in the SOVAI catalogue matches this address."
      path={slug ? `/datasets/${slug}` : '/datasets'}
      noIndex
    />
    <main>
      <div className="max-w-2xl">
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-gray-400">
          404
        </p>
        <h1 className="mt-3 text-3xl font-bold text-white md:text-4xl">
          Dataset not found
        </h1>
        <p className="mt-4 leading-relaxed text-gray-300">
          {slug ? (
            <>
              No dataset in the catalogue has the identifier{' '}
              <span className="font-mono text-gray-100">{slug}</span>.
            </>
          ) : (
            <>No dataset was specified in this address.</>
          )}{' '}
          It may have been renamed, or the link may be mistyped.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/datasets"
            className={`border border-blue-400/50 px-4 py-2 font-mono text-[12px] text-blue-300 hover:border-blue-400 hover:text-blue-200 ${FOCUS}`}
          >
            Browse all {datasets.length} datasets
          </Link>
          <a
            href="https://docs.sov.ai/realtime-datasets"
            target="_blank"
            rel="noopener noreferrer"
            className={`border border-gray-700 px-4 py-2 font-mono text-[12px] text-gray-300 hover:border-gray-500 hover:text-white ${FOCUS}`}
          >
            Documentation
          </a>
        </div>
      </div>
    </main>
  </Shell>
);

// ---- Page --------------------------------------------------------------------

export const DatasetDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const dataset = slug ? getDataset(slug) : undefined;

  const context = useMemo(
    () => (dataset ? buildCoverageContext(dataset) : []),
    [dataset],
  );
  const related = useMemo(
    () => (dataset ? relatedDatasets(dataset) : []),
    [dataset],
  );

  if (!dataset) return <NotFound slug={slug} />;

  return (
    <Shell>
      {/*
        Emits the schema.org `Dataset` node that Google Dataset Search indexes.
        Built from the registry, so undefined fields are omitted rather than
        guessed — the same honesty rule the visible page follows.
      */}
      <Seo
        title={dataset.name}
        description={dataset.summary}
        path={`/datasets/${dataset.slug}`}
        type="article"
        jsonLd={[
          datasetJsonLd(dataset),
          breadcrumbJsonLd([
            { name: 'Data catalogue', path: '/datasets' },
            { name: dataset.name, path: `/datasets/${dataset.slug}` },
          ]),
        ]}
      />

      <main>
        {/* ---- Identity ---------------------------------------------------- */}
        <nav aria-label="Breadcrumb" className="mb-5">
          <ol className="flex flex-wrap items-center gap-2 font-mono text-[12px] text-gray-400">
            <li>
              <Link to="/datasets" className={`hover:text-white ${FOCUS}`}>
                datasets
              </Link>
            </li>
            <li aria-hidden="true" className="text-gray-400">
              /
            </li>
            <li className="text-gray-300" aria-current="page">
              {dataset.slug}
            </li>
          </ol>
        </nav>

        <header className="border-b border-gray-800 pb-5">
          <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-2">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
                {dataset.name}
              </h1>
              <p className="mt-0.5 font-mono text-[11px] uppercase tracking-[0.14em] text-gray-400">
                {CATEGORY_LABEL[dataset.category]} dataset
              </p>
            </div>
            <a
              href={dataset.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`shrink-0 font-mono text-[12px] text-blue-300 underline underline-offset-4 hover:text-blue-200 ${FOCUS}`}
            >
              Documentation &rarr;
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </div>

          {/* Prose is the one place sans is allowed. */}
          <p className="mt-3 max-w-3xl text-[13px] leading-relaxed text-gray-300">
            {dataset.summary}
          </p>

          <IdentityChips dataset={dataset} />
          <Constituents dataset={dataset} />
        </header>

        <div className="mt-8 space-y-8">
          {/* ---- Specifications --------------------------------------------- */}
          <SpecificationsTable dataset={dataset} />

          {/* ---- Catalogue context ------------------------------------------ */}
          <Panel
            id="context-heading"
            label="In context"
            aside={`vs ${datasets.length} datasets`}
          >
            <dl className="grid gap-x-8 gap-y-0 sm:grid-cols-2">
              {context.map(note => (
                <div
                  key={note.label}
                  className="border-b border-gray-800 py-3 last:border-b-0 sm:[&:nth-last-child(2)]:border-b-0"
                >
                  <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-gray-400">
                    {note.label}
                    {note.leading ? (
                      <span className="ml-2 text-blue-400">
                        leads catalogue
                      </span>
                    ) : null}
                  </dt>
                  <dd
                    className={`mt-1 text-[13px] leading-relaxed ${
                      note.gap ? 'text-gray-400' : 'text-gray-300'
                    }`}
                  >
                    {note.text}
                  </dd>
                </div>
              ))}
            </dl>
          </Panel>

          {/* ---- Code sample ------------------------------------------------- */}
          <CodeSample dataset={dataset} />

          {/* ---- Documentation ----------------------------------------------- */}
          <Panel id="docs-heading" label="Reference">
            <p className="max-w-3xl leading-relaxed text-gray-300">
              Field definitions, methodology and worked examples for{' '}
              {dataset.name} live in the documentation. Where this page and the
              documentation disagree, the documentation is correct.
            </p>
            <p className="mt-3 break-all font-mono text-[13px]">
              <DataLink href={dataset.docsUrl}>{dataset.docsUrl}</DataLink>
            </p>
          </Panel>

          {/* ---- Related ------------------------------------------------------ */}
          {related.length > 0 && (
            <Panel
              id="related-heading"
              label={`Related · ${CATEGORY_LABEL[
                dataset.category
              ].toLowerCase()}`}
              aside={
                <Link to="/datasets" className={`hover:text-gray-300 ${FOCUS}`}>
                  All datasets &rarr;
                </Link>
              }
            >
              <ul className="divide-y divide-gray-800">
                {related.map(item => (
                  <li key={item.slug}>
                    <Link
                      to={`/datasets/${item.slug}`}
                      className={`group flex flex-wrap items-baseline gap-x-4 gap-y-1 py-3 ${FOCUS}`}
                    >
                      <span className="font-mono text-[13px] text-white group-hover:text-blue-300">
                        {item.name}
                      </span>
                      <span className="font-mono text-[11px] text-gray-400">
                        {item.historyStart
                          ? `since ${item.historyStart.slice(0, 4)}`
                          : 'start date not documented'}
                      </span>
                      <span className="w-full text-[13px] leading-relaxed text-gray-400 sm:w-auto sm:flex-1">
                        {item.summary}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      </main>
    </Shell>
  );
};

export default DatasetDetailPage;
